import { screenH, screenW } from "@massivewebgames/screen";

export interface Indicator {
  /** Client px; may well be off screen, which is the point. */
  x: number;
  y: number;
  kind: "pirate" | "crew" | "ship" | "whale";
  label?: string;
}

/** How far in from the window edge an arrow sits, and how far off screen something must be to get one (px). */
const MARGIN = 28;

/**
 * Arrows at the screen edge pointing at things worth knowing about that are
 * out of view: pirates, crewmates, your own ship, whales. Elements are kept
 * and reused frame to frame, and only moved with transforms, so this never
 * forces a layout.
 */
export class Indicators {
  private readonly root = document.createElement("div");
  private readonly pool: { el: HTMLElement; arrow: HTMLElement; label: HTMLElement; kind: string; text: string }[] = [];

  constructor() {
    this.root.id = "indicators";
    document.getElementById("hud")!.append(this.root);
  }

  update(items: Indicator[]): void {
    const w = screenW();
    const h = screenH();
    const cx = w / 2;
    const cy = h / 2;
    let used = 0;
    for (const it of items) {
      if (it.x >= -MARGIN && it.x <= w + MARGIN && it.y >= -MARGIN && it.y <= h + MARGIN) continue;
      // Walk from the centre toward it until we meet the inset edge.
      const dx = it.x - cx;
      const dy = it.y - cy;
      const t = Math.min(Math.abs((cx - MARGIN) / (dx || 1e-6)), Math.abs((cy - MARGIN) / (dy || 1e-6)));
      const x = cx + dx * t;
      const y = cy + dy * t;
      const p = this.pool[used] ?? this.add();
      used++;
      if (p.kind !== it.kind) {
        p.el.className = `indicator ${it.kind}`;
        p.kind = it.kind;
      }
      const text = it.label ?? "";
      if (p.text !== text) {
        p.label.textContent = text;
        p.text = text;
      }
      p.el.style.display = "";
      p.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      p.arrow.style.transform = `rotate(${Math.atan2(dy, dx).toFixed(3)}rad)`;
    }
    for (let i = used; i < this.pool.length; i++) this.pool[i]!.el.style.display = "none";
  }

  private add() {
    const el = document.createElement("div");
    const arrow = document.createElement("div");
    arrow.className = "indicator-arrow";
    const label = document.createElement("div");
    label.className = "indicator-label";
    el.append(arrow, label);
    this.root.append(el);
    const p = { el, arrow, label, kind: "", text: "" };
    this.pool.push(p);
    return p;
  }
}
